import { Suspense, lazy, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { uz } from '../i18n/uz';
import { RealtimeProvider } from '../lib/realtime/RealtimeProvider';
import { useRealtimeEvents } from '../lib/realtime/context';
import { Call } from '../screens/Call';
import { Create } from '../screens/Create';
import { Home } from '../screens/Home';
import { Match } from '../screens/Match';
import { Onboard } from '../screens/Onboard';
import { Chats } from '../screens/Chats';
import { Dm } from '../screens/Dm';
import { Profile } from '../screens/Profile';
import { ProfileEdit } from '../screens/ProfileEdit';
import { Search } from '../screens/Search';
import { Settings } from '../screens/Settings';
import { Top } from '../screens/Top';
import { Shop } from '../screens/Shop';
import { CTAButton, Toast } from '../ui';
import { Dock } from '../ui/Dock';
import { dockVisible } from '../ui/dockRoutes';
import { useToast } from '../ui/useToast';

/* Pattern galereyasi ALOHIDA chunkda — production'da hech qachon
 * yuklanmaydi, chunki marshrut ro'yxatdan o'tmaydi. */
const Patterns = lazy(() => import('../screens/Patterns').then((m) => ({ default: m.Patterns })));

/* `Room` LAZY — u `livekit-client` ni tortadi (~150 kB gzip). Statik
 * import bo'lganda kutubxona ASOSIY bundlega tushardi va roomga hech
 * qachon kirmagan foydalanuvchi ham uni yuklab olardi (audit T-033). */
const Room = lazy(() => import('../screens/Room').then((m) => ({ default: m.Room })));
import { useAuth } from './auth';
import { insideTelegram } from './telegram';

/**
 * Kirish darvozasi.
 *
 * Telegram tashqarisida ilova yuklanmaydi — bu UX, XAVFSIZLIK EMAS
 * (docs/SECURITY.md §5). Uni chetlab o'tish oson; maqsad tasodifiy
 * foydalanuvchini to'g'ri joyga yo'naltirish.
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const { status, error, login } = useAuth();

  // Dev build'da Telegramsiz ham kiramiz (docs/LOCAL_DEV.md Yo'l 1).
  // `import.meta.env.DEV` production bundle'da `false` — shart butunlay
  // yo'q bo'lib ketadi.
  const canLogin = insideTelegram || import.meta.env.DEV;

  useEffect(() => {
    if (status === 'idle' && canLogin) void login();
  }, [status, login, canLogin]);

  if (!canLogin) {
    return (
      <Centered>
        <div style={{ fontSize: 20, fontWeight: 800 }}>{uz.gate.onlyTelegram}</div>
        <div style={{ fontSize: 14, color: 'var(--fg-55)', marginTop: 10, lineHeight: 1.6 }}>
          {uz.gate.onlyTelegramHint}
        </div>
      </Centered>
    );
  }

  if (status === 'loading' || status === 'idle') {
    return (
      <Centered>
        <div style={{ color: 'var(--fg-55)', fontSize: 14 }}>{uz.common.loading}</div>
      </Centered>
    );
  }

  if (status === 'error') {
    return (
      <Centered>
        <div style={{ fontSize: 18, fontWeight: 800 }}>{uz.gate.authFailed}</div>
        <div style={{ fontSize: 13, color: 'var(--fg-45)', marginTop: 8 }}>{error}</div>
        <div style={{ marginTop: 20, width: '100%' }}>
          <CTAButton variant="ghost" onClick={() => void login()}>
            {uz.common.retry}
          </CTAButton>
        </div>
      </Centered>
    );
  }

  return <>{children}</>;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="screen"
      style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        paddingTop: 0,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Dock qatlami — marshrutdan YUQORIDA turadi.
 *
 * Nega ekranlar ichida emas: dock marshrutlar orasida qayta chizilmasligi
 * kerak. Har ekran o'zi chizsa u har o'tishda yo'qolib-paydo bo'lardi.
 *
 * Yopiq tugma bosilganda toast shu yerda ko'rsatiladi — dock ekranga
 * tegishli emas, shuning uchun ekranning toast'idan foydalana olmaydi.
 */
function DockLayer() {
  const { pathname } = useLocation();
  const toast = useToast();

  if (!dockVisible(pathname)) return null;

  return (
    <>
      {toast.message && <Toast message={toast.message} />}
      <Dock onLocked={toast.show} />
    </>
  );
}

/**
 * Yangi foydalanuvchini onboardingga yo'naltiradi.
 *
 * `isNew` `POST /auth/telegram` javobidan keladi — server "bu hisob
 * hozir yaratildi" deydi. Klientda "birinchi marta ochyaptimi" ni
 * aniqlashga urinish ishonchsiz: `CloudStorage` eski klientda
 * ishlamaydi (D-125) va qurilma almashtirilsa bayroq yo'qoladi.
 *
 * ⚠️ Bir marta va FAQAT `/` da: odam onboardingdan `O'tkazish` bilan
 * chiqsa uni qaytarib olib kelish tuzoq bo'lardi. `replace` ham shu
 * uchun — orqaga tugmasi onboardingga qaytmasin.
 */
function OnboardingRedirect() {
  const { status, isNew } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const sent = useRef(false);

  useEffect(() => {
    if (status !== 'ready' || !isNew || sent.current || pathname !== '/') return;
    sent.current = true;
    navigate('/onboard', { replace: true });
  }, [status, isNew, pathname, navigate]);

  return null;
}

/**
 * `match.found` — ILOVA darajasida, ekranda emas.
 *
 * ⚠️ **Nega bu yerda.** `RealtimeProvider` izohi WS ULANISHI nega
 * ilova darajasida ekanini tushuntiradi. Lekin HANDLER `Search`
 * ekranida qolgan edi va bu o'sha xatoning ikkinchi yarmi:
 *
 *     1. B `POST /match/search` yuboradi
 *     2. server juftlikni topadi va `match.found` ni DARHOL chiqaradi
 *     3. B hali `/match` da — `/search` ekrani MOUNT bo'lmagan
 *     4. `Search` dagi tinglovchi hali obuna emas → event yo'qoladi
 *
 * Natijada **juftlikni yopgan odam radar ekranida qolib ketardi** va
 * faqat timeout'da (pul qaytarilib) chiqardi. Suhbatdoshi esa
 * qo'ng'iroq ekranida yolg'iz kutardi.
 *
 * `Match.tsx` xuddi shu muammoni `match.queued` uchun navigatsiya
 * holati bilan hal qilgan — `match.found` esa e'tibordan chetda qolgan.
 */
function MatchFoundRedirect() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // `pathname` to'g'ridan-to'g'ri: `useRealtimeEvents` handlerni HAR
  // renderda yangilaydi, ya'ni yopilma eskirmaydi va `ref` kerak emas.
  useRealtimeEvents((event) => {
    if (event.type !== 'match.found') return;
    // Allaqachon qo'ng'iroqdamiz — ikkinchi navigatsiya ekranni
    // qayta yaratardi va taymer noldan boshlanardi.
    if (pathname.startsWith('/call')) return;
    navigate('/call', { state: { match: event }, replace: true });
  });

  return null;
}

export function App() {
  // WS marshrutlardan YUQORIDA. `AuthGate` har marshrutda alohida
  // ishlaydi, ya'ni provider u yerda tursa har navigatsiyada ulanish
  // uzilib qayta tiklanardi — va aynan o'sha oynada `match.found`
  // o'tib ketardi (RealtimeProvider izohiga qarang).
  const { status } = useAuth();

  return (
    <RealtimeProvider enabled={status === 'ready'}>
      <OnboardingRedirect />
      <MatchFoundRedirect />
      <DockLayer />
      <Routes>
        {/* Pattern galereyasi — ISHLAB CHIQISH vositasi.
            Ikki qavat: marshrut faqat DEV'da ro'yxatdan o'tadi, moduli
            esa `lazy` bilan ALOHIDA chunkda. Statik import bo'lganda
            shart `false` bo'lsa ham modul bundle'da qolardi — buni
            tekshirib ko'rdim, tree-shaking uni olib tashlamaydi
            (CSS importi tufayli modul "yon ta'sirli" hisoblanadi). */}
        {import.meta.env.DEV && (
          <Route
            path="/patterns"
            element={
              <Suspense fallback={null}>
                <Patterns />
              </Suspense>
            }
          />
        )}
        <Route
          path="/"
          element={
            <AuthGate>
              <Home />
            </AuthGate>
          }
        />
        <Route
          path="/onboard"
          element={
            <AuthGate>
              <Onboard />
            </AuthGate>
          }
        />
        <Route
          path="/profile"
          element={
            <AuthGate>
              <Profile />
            </AuthGate>
          }
        />
        <Route
          path="/chats"
          element={
            <AuthGate>
              <Chats />
            </AuthGate>
          }
        />
        <Route
          path="/dm/:id"
          element={
            <AuthGate>
              <Dm />
            </AuthGate>
          }
        />
        <Route
          path="/profile/edit"
          element={
            <AuthGate>
              <ProfileEdit />
            </AuthGate>
          }
        />
        <Route
          path="/settings"
          element={
            <AuthGate>
              <Settings />
            </AuthGate>
          }
        />
        <Route
          path="/shop"
          element={
            <AuthGate>
              <Shop />
            </AuthGate>
          }
        />
        <Route
          path="/top"
          element={
            <AuthGate>
              <Top />
            </AuthGate>
          }
        />
        <Route
          path="/create"
          element={
            <AuthGate>
              <Create />
            </AuthGate>
          }
        />
        <Route
          path="/room/:id"
          element={
            <AuthGate>
              <Suspense fallback={<div className="screen muted">{uz.common.loading}</div>}>
                <Room />
              </Suspense>
            </AuthGate>
          }
        />
        <Route
          path="/match"
          element={
            <AuthGate>
              <Match />
            </AuthGate>
          }
        />
        <Route
          path="/search"
          element={
            <AuthGate>
              <Search />
            </AuthGate>
          }
        />
        <Route
          path="/call"
          element={
            <AuthGate>
              <Call />
            </AuthGate>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </RealtimeProvider>
  );
}
